import type { ICampaignAnalyticMetricDetect } from '../hooks';

import { EMPTY_METRIC_DATA } from '~/modules/campaign';
import { ICampaignMetricKey } from '../types';

/**
 * Bounce rate is a "lower is better" metric. Compared against the enum member
 * (not a string) so renaming/removing BOUNCE_RATE is a compile error here,
 * not a silently-broken color/winner detection.
 */
export const isReverseCampaignMetric = (metricKey?: ICampaignMetricKey): boolean => {
  return metricKey === ICampaignMetricKey.BOUNCE_RATE;
};

export const determineMetricWinner = (
  metricA: ICampaignAnalyticMetricDetect,
  metricB: ICampaignAnalyticMetricDetect,
  metricKey: keyof ICampaignAnalyticMetricDetect,
) => {
  const valueA = metricA[metricKey].value;
  const valueB = metricB[metricKey].value;

  const isNoResult = valueA === EMPTY_METRIC_DATA || valueB === EMPTY_METRIC_DATA;
  if (isNoResult) {
    return {
      isVariantALarger: false,
      isVariantBLarger: false,
      isBothSimilar: false,
      isNoResult,
    };
  }

  const numA = typeof valueA === 'string' ? parseFloat(valueA) || 0 : Number(valueA);
  const numB = typeof valueB === 'string' ? parseFloat(valueB) || 0 : Number(valueB);

  const isLowerBetter = metricKey === 'bounceRateMetric';
  const [isVariantALarger, isVariantBLarger, isBothSimilar] = isLowerBetter
    ? [numA < numB, numB < numA, numA === numB]
    : [numA > numB, numB > numA, numA === numB];

  if (isBothSimilar) {
    return {
      isVariantALarger: false,
      isVariantBLarger: false,
      isBothSimilar,
      isNoResult: false,
    };
  }

  return {
    isVariantALarger,
    isVariantBLarger,
    isBothSimilar: false,
    isNoResult: false,
  };
};

/**
 * Formats an array of metric names into a human-readable string
 * @param metricNames - Array of metric names (e.g., ['Conversion rate', 'Revenue per visitor'])
 * @param fallback - Fallback text for empty arrays (default: 'no metrics')
 * @returns A string like 'Conversion rate, Revenue per visitor & Average order value'
 */
export const formatMetricNames = (metricNames: string[], fallback = 'no metrics'): string => {
  // Filter out undefined/null values and ensure all items are strings
  const validNames = metricNames.filter((name): name is string => typeof name === 'string');

  // Handle edge cases
  if (validNames.length === 0) {
    return fallback;
  }
  if (validNames.length === 1) {
    return validNames[0];
  }
  if (validNames.length === 2) {
    return `${validNames[0]} & ${validNames[1]}`;
  }

  // For 3 or more items: join all but the last with commas, then add " & " last
  return `${validNames.slice(0, -1).join(', ')} & ${validNames[validNames.length - 1]}`;
};
